import React, { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

const STAGES = [
  {
    image: 'https://customer-assets.emergentagent.com/job_827345f2-6547-45f4-8537-fd6820f9b630/artifacts/6yeybqnl_nEHM%20ICH%20ERSTMAL.png',
    eyebrow: 'Schritt 01',
    title: 'PDF rein. Plan raus.',
    description:
      'Ihr Grundriss wird in unter 10 Sekunden zerlegt: Räume, Wände, Türen, Fenster und alle Maßketten werden automatisch erkannt – pixelgenau und auf den Vektoren Ihres Original-PDFs aufgesetzt.',
    bullets: [
      'Maßketten direkt aus PDF-Vektoren ausgelesen',
      'Pixelgenaue Skalenkalibrierung',
      'Polygon-Geometrie für jede Wand und jeden Raum',
    ],
    maxWidth: 520,
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_827345f2-6547-45f4-8537-fd6820f9b630/artifacts/io344u6e_GUTTTTT.png',
    eyebrow: 'Schritt 02',
    title: 'Außen ist nicht Innen.',
    description:
      'Schon im ersten Moment trennt die Engine Außenwände von Innenwänden – ohne dass Sie eine einzige Wand selbst markieren müssen. Wandstärken werden auf DIN-Standardwerte gerundet, Räume klassifiziert.',
    bullets: [
      'Automatische AW/IW-Klassifizierung über die Gebäudehülle',
      'Wandstärken pixelgenau gemessen + DIN-Snap',
      'Räume mit Fläche, Umfang und Volumen sofort verfügbar',
    ],
    maxWidth: 520,
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_827345f2-6547-45f4-8537-fd6820f9b630/artifacts/vklf0vpd_Schnitt.png',
    eyebrow: 'Schritt 03',
    title: 'Schnitt erkannt. Schnitt verstanden.',
    description:
      'Laden Sie zusätzlich einen Gebäudeschnitt hoch – die Engine analysiert ihn automatisch, extrahiert Geschosshöhen, BRH, STH und Wandaufbauten und überträgt diese auf den passenden Grundriss.',
    bullets: [
      'Automatische Schnitt-Analyse',
      'Geschosshöhen automatisch je Etage zugeordnet',
      'Fenster- und Türhöhen aus dem Schnitt synchronisiert',
    ],
    // Kleiner UI-Screenshot — niedrige native Auflösung, nicht hochskalieren
    maxWidth: 380,
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_827345f2-6547-45f4-8537-fd6820f9b630/artifacts/oudo0eku_tOOLBAR.png',
    eyebrow: 'Schritt 04',
    title: 'Power-Tools für Profis.',
    description:
      'Multi-Selektion mit Ctrl+Klick, Format-Übertragung per Pinsel, intelligente Validierung der Öffnungen – alle Werkzeuge die Architekten brauchen, sauber organisiert in einem einklappbaren Eigenschaften-Panel.',
    bullets: [
      'Ctrl+Klick Mehrfachauswahl + Massen-Edit',
      'Automatische Öffnungs-Validierung (Typ, Maße, Anschlag)',
      'Werkzeugkasten einklappbar – kein UI-Lärm',
    ],
    // Schmales Side-Panel — eher hoch als breit
    maxWidth: 360,
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_827345f2-6547-45f4-8537-fd6820f9b630/artifacts/3qnwjkky_eRSTELLUNG.png',
    eyebrow: 'Schritt 05',
    title: 'Vom Plan zum LV — ein Klick.',
    description:
      'Alle erkannten Bauteile fließen direkt in ein professionelles Langtext-Leistungsverzeichnis nach VOB/C. Wandvolumen, Wandfläche, Türen, Fenster, Bodenflächen – fertig zur Vergabe.',
    bullets: [
      'Wandvolumen + Wandfläche je Gruppe (m³ + m²)',
      'Manuelle Positionen mit L/B/H-Rechner',
      'PDF-Export ready für die Vergabe',
    ],
    maxWidth: 560,
  },
];

function StageCard({ stage, index, reverse }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`engine-stage-${index}`}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* TEXT */}
      <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">
          {stage.eyebrow}
        </div>
        <h3
          className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {stage.title}
        </h3>
        <p className="text-zinc-400 leading-relaxed mb-5">{stage.description}</p>
        <ul className="space-y-2.5">
          {stage.bullets.map((b, j) => (
            <li key={j} className="flex items-start gap-3 text-sm text-zinc-300">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* IMAGE */}
      <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : 'lg:order-2'} flex justify-center`}>
        <div
          className="relative rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 shadow-2xl shadow-blue-500/5 w-full"
          style={{ maxWidth: stage.maxWidth || 520 }}
        >
          {/* Window-chrome */}
          <div className="absolute top-2.5 left-2.5 z-10 flex gap-1.5">
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
          </div>
          <div className="absolute top-2.5 right-2.5 z-10 px-2 py-0.5 bg-zinc-950/70 backdrop-blur border border-zinc-700/50 rounded font-mono text-[9px] text-zinc-300">
            {String(index + 1).padStart(2, '0')} / {String(STAGES.length).padStart(2, '0')}
          </div>
          <img
            src={stage.image}
            alt={stage.title}
            loading="lazy"
            className="w-full h-auto block bg-zinc-950"
            style={{
              maxHeight: stage.maxHeight || 'none',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function EngineShowcase() {
  return (
    <section
      id="engine"
      className="relative py-24 px-6 overflow-hidden"
      data-testid="engine-showcase"
    >
      {/* Background removed — unified dark */}

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-xs font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5" /> So arbeitet unsere Engine
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Vom PDF-Grundriss
            <br />
            zum vergabefertigen LV.
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed">
            Fünf Stationen unserer Pipeline – jeder Screenshot stammt aus einer echten Kundenanalyse.
          </p>
        </div>

        {/* Stage list — alternating left/right */}
        <div className="space-y-28 lg:space-y-36">
          {STAGES.map((s, i) => (
            <StageCard key={i} stage={s} index={i} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
